import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Roles } from "../../constants/roles";
import { anatomyConstants } from "../../constants/anatomyConstants";

const UserPermissionModal = ({
  onClose,
  selectedCase,
  userRole,
  onRoleChange,
  handleUserDelete,
}) => {
  const currentUserId = useSelector((state) => state.user.currentUser.id);
  const roleOrder = [
    Roles.ADMIN,
    Roles.CREATOR,
    Roles.OWNER,
    Roles.EDITOR,
    Roles.VIEWER,
  ];

  const [localUsers, setLocalUsers] = useState({});

  useEffect(() => {
    setLocalUsers({ ...selectedCase?.users });
  }, [selectedCase]);

  const sortedUsers = Object.entries(localUsers).sort(
    ([, userInfoA], [, userInfoB]) =>
      roleOrder.indexOf(userInfoA.role) - roleOrder.indexOf(userInfoB.role)
  );

  const canModifyRole = (targetRole) => {
    if (userRole === Roles.ADMIN || userRole === Roles.CREATOR) {
      return [Roles.OWNER, Roles.EDITOR, Roles.VIEWER].includes(targetRole);
    }
    if (userRole === Roles.OWNER) {
      return [Roles.EDITOR, Roles.VIEWER].includes(targetRole);
    }
    return false;
  };

  const getEditableRoles = () => {
    if (userRole === Roles.ADMIN || userRole === Roles.CREATOR) {
      return [Roles.OWNER, Roles.EDITOR, Roles.VIEWER];
    }
    if (userRole === Roles.OWNER) {
      return [Roles.EDITOR, Roles.VIEWER];
    }
    return [];
  };

  const editableRoles = getEditableRoles();

  const canDeleteUser = (targetRole, targetUserId) => {
    if ([Roles.EDITOR, Roles.VIEWER].includes(userRole)) return false;
    if (targetUserId === currentUserId) return false;
    const currentIndex = roleOrder.indexOf(userRole);
    const targetIndex = roleOrder.indexOf(targetRole);
    return currentIndex < targetIndex;
  };

  const handleRoleChangeLocal = (userId, newRole) => {
    setLocalUsers((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        role: newRole,
      },
    }));
  };

  const handleSaveChanges = () => {
    onRoleChange(localUsers);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div className="bg-white p-8 rounded shadow-lg w-full max-w-sm">
        <h2 className="text-xl font-semibold mb-4 text-[var(--main-color)]">
          User Permissions
        </h2>
        <div className="space-y-2">
          {sortedUsers.map(([userId, userInfo]) => (
            <div key={userId} className="flex justify-between items-center">
              <div className="flex items-center">
                {canDeleteUser(userInfo.role, userId) && (
                  <button
                    onClick={() => handleUserDelete(userId)}
                    className="mr-2 text-red-500 hover:text-red-700 focus:outline-none"
                  >
                    ✖
                  </button>
                )}
                <span className="font-medium">
                  {userInfo.name || "Unknown User"}
                </span>
              </div>
              {canModifyRole(userInfo.role) ? (
                <select
                  className="mt-1 border-gray-300 rounded-md shadow-sm focus:ring-[var(--main-color)] focus:border-[var(--main-color)] sm:text-sm text-gray-700 p-1"
                  style={{ minWidth: "100px", maxWidth: "200px" }}
                  value={userInfo.role}
                  onChange={(e) =>
                    handleRoleChangeLocal(userId, e.target.value)
                  }
                >
                  {editableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-gray-500">{userInfo.role}</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4 space-x-4">
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition focus:outline-none"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            className="bg-[var(--main-color)] text-white px-4 py-2 rounded hover:bg-[var(--main-color-bold)] transition focus:outline-none"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPermissionModal;
